const createId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const formatOpeningModes = {
  layer_by_layer: "layered_gift",
  memory_page: "classic",
  gift_reveal: "gift_unboxing",
  photo_journey: "photo_album",
  letters_for_later: "envelope",
  keepsake_moment: "poster",
  surprise_reveal: "surprise_lock",
};

const baseData = (ownerName, openingTitle, openingSubtitle, themeId, giftFormat = "gift_reveal") => ({
  brandId: "momgifts",
  ownerName,
  openingTitle,
  openingSubtitle,
  giftFormat,
  openingMode: formatOpeningModes[giftFormat] || "layered_gift",
  themeId,
  viewMode: "list",
  productType: "nfc_memory_gift",
  ritualMode: "sealed_gift",
  ritualText: "Tap the NFC gift, open the story, and keep the moment.",
  status: "active",
});

const mediaModule = (caption = "", title = "") => ({
  id: createId("media"),
  type: "primary_media",
  data: {
    ...(title ? { title } : {}),
    mediaType: "photo",
    images: [],
    caption,
    videoCaption: "",
  },
});

const textModule = (title, body) => ({
  id: createId("text"),
  type: "text",
  data: { title, body },
});

const surpriseModule = (label, content, hint) => {
  const letters = Array.isArray(content)
    ? content.map((letter) => ({
      id: createId("open_when"),
      label: letter.label || label,
      prompt: letter.prompt || hint,
      message: letter.message || "",
    }))
    : [
      {
        id: createId("open_when"),
        label,
        prompt: hint,
        message: content,
      },
    ];
  const firstLetter = letters[0] || { label, prompt: hint, message: "" };

  return {
    id: createId("surprise"),
    type: "surprise_box",
    data: {
      label: firstLetter.label,
      hint: firstLetter.prompt,
      content: firstLetter.message,
      letters,
    },
  };
};

const voiceModule = (title, prompt = "Record a short message they can replay later.") => ({
  id: createId("voice"),
  type: "voice_postcard",
  data: {
    title,
    prompt,
    audioUrl: null,
    recordedUrl: null,
  },
});

const timelineModule = (title, items = []) => ({
  id: createId("timeline"),
  type: "memory_map",
  data: {
    title,
    timelineItems: items.map((item) => ({
      ...item,
      note: item.note || "",
    })),
  },
});

const growthTimelineModule = (title, items = []) => ({
  id: createId("growth_timeline"),
  type: "timeline",
  data: {
    title,
    timelineItems: items.map((item) => ({
      ...item,
      note: item.note || "",
    })),
  },
});

const babyMilestonesModule = (title = "Milestones", subtitle = "Tiny firsts, family moments, and little dates worth saving.", stops = []) => ({
  id: createId("baby_milestones"),
  type: "memory_map",
  data: {
    title,
    subtitle,
    displayKicker: "Milestone path",
    stops: stops.map((stop, index) => ({
      id: stop.id || createId("milestone"),
      place: stop.place || stop.label || `Milestone ${index + 1}`,
      date: stop.date || stop.dateLabel || "",
      note: stop.note || "",
    })),
    timelineItems: stops.map((stop, index) => ({
      id: stop.id || createId("milestone"),
      label: stop.place || stop.label || `Milestone ${index + 1}`,
      dateLabel: stop.date || stop.dateLabel || "",
      note: stop.note || "",
      iconType: stop.iconType || "heart",
      linkedModuleId: stop.linkedModuleId || "",
    })),
  },
});

const thenNowModule = (
  title = "Then & Now",
  subtitle = "A small look at how far this story has come."
) => ({
  id: createId("then_now"),
  type: "then_now",
  data: {
    title,
    subtitle,
    beforeImage: "",
    afterImage: "",
  },
});

const tinyMuseumModule = (
  title = "Tiny Museum",
  subtitle = "A quiet gallery of details, dates, and small keepsakes.",
  exhibits = []
) => ({
  id: createId("museum"),
  type: "tiny_museum",
  data: {
    title,
    subtitle,
    exhibits: exhibits.map((exhibit, index) => ({
      id: exhibit.id || createId("exhibit"),
      title: exhibit.title || `Exhibit ${index + 1}`,
      date: exhibit.date || "",
      note: exhibit.note || "",
      imageUrl: exhibit.imageUrl || "",
    })),
  },
});

const reactionModule = (title, subtitle, label = "Send a Reply") => ({
  id: createId("reaction"),
  type: "polaroid_booth",
  data: { label, title, subtitle },
});

const guestbookModule = (title = "Blessing Wall", overrides = {}) => ({
  id: createId("guestbook"),
  type: "guestbook",
  data: {
    title,
    subtitle: "Notes from people invited into this gift.",
    ...overrides,
  },
});

const finalKeepsakeModule = (
  title,
  senderName = "Someone Special",
  message = "A small piece of this moment, kept in one place.",
  closingLine = "Come back whenever you need to remember this."
) => ({
  id: createId("poster"),
  type: "keepsake_poster",
  data: {
    title,
    senderName,
    message,
    closingLine,
    posterStyle: "macaron",
  },
});

export const DEFAULT_TEMPLATES = {
  mom: {
    data: baseData("Mom", "For Mom", "A few small ways to say thank you", "pearl_rose", "layer_by_layer"),
    modules: [
      mediaModule("Begin with the everyday photo that feels like home: a kitchen laugh, a holiday table, a road trip, or one quiet moment with her."),
      textModule(
        "For Everything You Made Feel Easy",
        "Mom, this is for the care I did not always notice in the moment: the rides, the reminders, the meals, the patience, and the way you kept showing up. So much of who I am was built from ordinary days you made safer, warmer, and better."
      ),
      surpriseModule(
        "Open When",
        [
          {
            label: "Open When",
            prompt: "Open when you wonder if it mattered",
            message: "It mattered. The small things, the repeated things, the things nobody clapped for. I carry more of your love than I probably say out loud.",
          },
          {
            label: "Open Later",
            prompt: "Open when the day feels heavy",
            message: "I hope this brings you back to the truth: you have been the steady place in so many memories, and you deserve to feel held too.",
          },
          {
            label: "Open Anytime",
            prompt: "Open when you want to smile",
            message: "One tiny proof that you are loved on normal days, not only holidays.",
          },
        ],
        "Open when you wonder if it mattered"
      ),
      voiceModule("A Voice Note for Mom", "Record the part that would mean more in your real voice: thank you, I love you, or the memory she should hear again."),
      guestbookModule("Family Blessing Wall"),
      finalKeepsakeModule(
        "Keep This Close",
        "With Love",
        "For every time you need proof that your love has been seen.",
        "Come back here whenever you need to feel appreciated."
      ),
    ],
  },

  partner: {
    data: baseData("My Love", "For Us", "A little love story to open slowly", "editorial_romance", "layer_by_layer"),
    modules: [
      mediaModule("Start with the photo or clip that instantly feels like the two of you: a trip, a couch night, a street corner, or a private joke."),
      textModule(
        "What I Keep Choosing",
        "I love the version of life that exists around you: the small routines, the private language, and the way the day gets softer when I know I get to tell you about it. This is a little place for the pieces of us I never want to treat as ordinary."
      ),
      thenNowModule(
        "Then & Now",
        "Add two photos that show how this love has grown: where it started, and what it feels like now."
      ),
      surpriseModule(
        "Open When",
        [
          {
            label: "Open When",
            prompt: "Open when you miss us",
            message: "Even when we are apart, the best parts of us are still real: the jokes, the comfort, and the way ordinary time feels different with you.",
          },
          {
            label: "Open Later",
            prompt: "Open after a long day",
            message: "I hope this feels like me reaching for your hand and reminding you that you do not have to carry everything alone.",
          },
        ],
        "Open when you miss us"
      ),
      timelineModule("Our Little Timeline", [
        {
          id: createId("tl"),
          label: "Where it started",
          dateLabel: "",
          note: "Add the first moment that made the story feel different.",
          iconType: "heart",
          linkedModuleId: "",
        },
        {
          id: createId("tl"),
          label: "A day I still replay",
          dateLabel: "",
          note: "Add the photo, place, line, or ordinary day that still feels vivid.",
          iconType: "photo",
          linkedModuleId: "",
        },
      ]),
      voiceModule("Listen When You Miss Me", "Record the message they should hear when they miss you."),
      finalKeepsakeModule(
        "Keep This Moment",
        "From Someone Who Loves You",
        "A little piece of us, saved inside this gift for the days we want to remember what we are building.",
        "Open it again whenever you need to feel close."
      ),
    ],
  },

  wedding: {
    data: baseData(
      "Ava & Julian",
      "A little favor from our day",
      "Thank you for being here. Open the story, sign the blessing wall, and keep this as a tiny piece of tonight.",
      "wedding_favor",
      "layer_by_layer"
    ),
    modules: [
      mediaModule(
        "Begin with the photo that feels like the day: the couple, the tables, the flowers, the venue, or one quiet moment before the celebration."
      ),
      textModule(
        "Thank You for Being Here",
        "Thank you for being part of the room where this promise became real. We made this little digital favor so you can open a few pieces of the day, leave us a blessing, and keep a small memory from the celebration."
      ),
      timelineModule("From Ceremony to Celebration", [
        {
          id: createId("tl"),
          label: "Ceremony",
          dateLabel: "",
          note: "Add the moment, place, or photo where the promise was made.",
          iconType: "heart",
          linkedModuleId: "",
        },
        {
          id: createId("tl"),
          label: "Reception",
          dateLabel: "",
          note: "Add the table, toast, dance floor, or small detail guests will remember.",
          iconType: "photo",
          linkedModuleId: "",
        },
      ]),
      surpriseModule(
        "Open When",
        [
          {
            label: "Open Tonight",
            prompt: "Open after the last song",
            message: "Thank you for celebrating with us. Your presence made the room feel warmer, louder, and more full of love.",
          },
          {
            label: "Open Later",
            prompt: "Open on our first anniversary",
            message: "A small reminder that this day was not only ours. It was held by everyone who showed up, cheered, danced, and wished us well.",
          },
        ],
        "Open after the last song"
      ),
      {
        id: createId("guestbook"),
        type: "guestbook",
        data: {
          title: "Wedding Blessing Wall",
          subtitle: "Leave a wish, memory, toast, or tiny note from your table.",
          displayLabel: "Guestbook",
          formTitle: "Leave a blessing",
          formDescription: "Write a short wish for the couple. Messages can be reviewed before they appear publicly.",
          successTitle: "Thank you for the blessing",
          successMessage: "Your note has been saved for the couple to keep with this wedding favor.",
        },
      },
      finalKeepsakeModule(
        "Keep This Favor",
        "Ava & Julian",
        "Thank you for holding this day with us.",
        "Save this little card as a piece of the celebration."
      ),
    ],
  },

  bestie: {
    data: baseData("Bestie", "The Map of Us", "A tiny archive of our photos, places, jokes, and chaos", "friends_graduation", "layer_by_layer"),
    modules: [
      mediaModule("Start with the photo that would make both of you say, 'no one else gets this.'"),
      timelineModule("Friendship Highlights", [
        {
          id: createId("tl"),
          label: "A peak bestie moment",
          dateLabel: "",
          note: "Add the moment that proves this friendship has its own language.",
          iconType: "photo",
          linkedModuleId: "",
        },
        {
          id: createId("tl"),
          label: "An inside joke",
          dateLabel: "",
          note: "Write the joke, screenshot, phrase, or disaster story nobody else would understand.",
          iconType: "quote",
          linkedModuleId: "",
        },
      ]),
      textModule(
        "Our Kind of Chaos",
        "Some friendships feel like a safe place and a terrible idea at the same time. This is for the saves, the voice notes, the questionable plans, and the memories no one else would understand."
      ),
      surpriseModule(
        "Open for Chaos",
        "Official reminder: this friendship deserves its own documentary, several deleted scenes, and at least one public apology.",
        "Open for an inside-joke moment"
      ),
      reactionModule("Send a Reply", "Drop an emoji, selfie, or quick video reaction", "Reply Back"),
      guestbookModule("Bestie Blessing Wall"),
      finalKeepsakeModule(
        "Keep This Friendship",
        "Your Favorite Person",
        "For the memories that would make no sense to anyone else, which is exactly why they matter.",
        "Save this for the next time we need proof that we are iconic."
      ),
    ],
  },

  birthday: {
    data: baseData("Birthday Star", "Happy Birthday", "A celebration made just for you", "pearl_rose", "layer_by_layer"),
    modules: [
      mediaModule("Begin with the photo or video that feels like the birthday person at full volume."),
      textModule(
        "Before the Wishes",
        "Today is a good excuse to say the part people should say more often: you make life brighter, funnier, softer, and better by being here."
      ),
      surpriseModule(
        "Birthday Surprise",
        "You deserve a day that feels loud in the best way and soft in all the right places. Let this be one more little spark in it.",
        "Open before the wishes"
      ),
      voiceModule("A Birthday Voice Note", "Record a birthday message they can replay after the candles, texts, and noise fade."),
      reactionModule("Send Birthday Love", "Add a quick selfie, emoji, or video wish", "Send a Wish"),
      guestbookModule("Birthday Blessing Wall"),
      finalKeepsakeModule(
        "Keep This Birthday",
        "From Your People",
        "One more wish to keep after the day is over.",
        "Come back when you want to remember how loved you are."
      ),
    ],
  },

  graduation: {
    data: baseData("Graduate", "The Map of Us", "A Polaroid-style chapter worth keeping", "friends_graduation", "layer_by_layer"),
    modules: [
      mediaModule("Start with the photo that says: this really happened."),
      timelineModule("Milestones", [
        {
          id: createId("tl"),
          label: "The beginning",
          dateLabel: "",
          note: "Add where this chapter started, before the finish line was visible.",
          iconType: "clock",
          linkedModuleId: "",
        },
        {
          id: createId("tl"),
          label: "The moment it became real",
          dateLabel: "",
          note: "Add the moment that made all the work feel worth it.",
          iconType: "heart",
          linkedModuleId: "",
        },
      ]),
      tinyMuseumModule("Tiny Museum of This Chapter", "Save a few details from this season before the next one begins.", [
        {
          title: "A detail from the journey",
          date: "",
          note: "Add a ticket, campus corner, note, photo, or small proof of the work it took.",
        },
        {
          title: "Proof you kept going",
          date: "",
          note: "Add something that reminds them this achievement was earned.",
        },
      ]),
      textModule(
        "You Earned This",
        "This gift is for the work people saw, the work they did not see, and the version of you that kept going anyway. This is not just an ending. It is proof that you can begin again with evidence behind you."
      ),
      surpriseModule(
        "For Your Next Chapter",
        "Open this when the next step feels big: you have already done hard things, and you know how to begin before you feel ready.",
        "Open when the next step feels big"
      ),
      voiceModule("A Proud Voice Note", "Record the proud part out loud. They may need to hear it again later."),
      guestbookModule("Congrats Blessing Wall"),
      finalKeepsakeModule(
        "Keep This Moment",
        "With Pride",
        "A reminder that this chapter was earned.",
        "Open this again when the next mountain looks impossible."
      ),
    ],
  },

  remembrance: {
    data: baseData(
      "Forever Remembered",
      "A Gentle Place to Remember",
      "A quiet archive for memories, voices, timelines, and family messages.",
      "quiet_keepsake",
      "layer_by_layer"
    ),
    modules: [
      mediaModule(
        "Begin with photos or short clips that feel true to their life: a favorite place, a family table, a smile, a detail, or a moment everyone still remembers.",
        "Memory Archive"
      ),
      voiceModule(
        "Voice Archive",
        "Record a memory, a story, a blessing, or the words the family may want to hear again later."
      ),
      growthTimelineModule("Timeline", [
        {
          id: createId("remembrance_tl"),
          label: "A beginning worth remembering",
          dateLabel: "",
          note: "Add an early memory, place, or family story that helps begin the archive gently.",
          iconType: "heart",
          linkedModuleId: "",
        },
        {
          id: createId("remembrance_tl"),
          label: "A life-giving memory",
          dateLabel: "",
          note: "Save a moment that shows their personality, care, humor, strength, or everyday presence.",
          iconType: "photo",
          linkedModuleId: "",
        },
        {
          id: createId("remembrance_tl"),
          label: "What we carry forward",
          dateLabel: "",
          note: "Add the lesson, tradition, phrase, or love that family keeps carrying from here.",
          iconType: "quote",
          linkedModuleId: "",
        },
      ]),
      guestbookModule("Family Messages", {
        subtitle: "Gentle notes, memories, and words from family and close loved ones.",
        displayLabel: "Family Messages",
        formTitle: "Leave a memory",
        formDescription: "Write a short memory, blessing, or message for the family archive.",
        successTitle: "Message saved",
        successMessage: "Your memory has been saved with this remembrance keepsake.",
      }),
      finalKeepsakeModule(
        "Keep This Memory",
        "With Love",
        "A quiet place for the stories, voices, and messages that still matter.",
        "Return here whenever remembering needs a gentle place to land."
      ),
    ],
  },

  pet_memorial: {
    data: baseData("Forever Loved", "Forever Loved", "A soft place to remember", "quiet_keepsake", "layer_by_layer"),
    modules: [
      mediaModule("Begin with the photo that still feels full of personality: the look, the spot, the tiny habit everyone remembers."),
      textModule(
        "The Little Things",
        "This page is for the small things that made a life feel full: the familiar sounds, the favorite places, the routines, and the kind of love that keeps showing up in the quiet."
      ),
      tinyMuseumModule("Tiny Museum of Love", "A soft place for the little details that should not disappear.", [
        {
          title: "A favorite little habit",
          date: "",
          note: "Add the tiny behavior everyone remembers.",
        },
        {
          title: "A memory we keep",
          date: "",
          note: "Add a quiet moment, photo, or place.",
        },
      ]),
      voiceModule("A Soft Memory", "Record a soft memory, a favorite habit, or the thing you still want to say."),
      finalKeepsakeModule(
        "Always Remembered",
        "Forever Loved",
        "The love stays, even in the quiet places.",
        "Return here when you want to feel close again."
      ),
    ],
  },

  new_baby: {
    data: baseData("Little One", "A Tiny Book of Firsts", "A soft album for milestones, wishes, photos, and the story of growing.", "soft_family", "layer_by_layer"),
    modules: [
      babyMilestonesModule("Milestones", "Tiny firsts, family moments, and little dates worth saving.", [
        {
          id: createId("milestone"),
          place: "First hello",
          date: "",
          note: "Add the first photo, tiny hand, hospital moment, or the day everyone finally met.",
          iconType: "heart",
        },
        {
          id: createId("milestone"),
          place: "First family moment",
          date: "",
          note: "Save the first quiet family photo, homecoming, sibling meeting, or favorite early routine.",
          iconType: "photo",
        },
        {
          id: createId("milestone"),
          place: "A tiny first",
          date: "",
          note: "Add the first smile, first laugh, first trip, first holiday, or another small beginning.",
          iconType: "clock",
        },
      ]),
      guestbookModule("Family Wishes", {
        subtitle: "Warm notes from family and loved ones for this little beginning.",
        displayLabel: "Family Wishes",
        formTitle: "Leave a wish",
        formDescription: "Write a short blessing, hope, or welcome note for the baby and family.",
        successTitle: "Wish saved",
        successMessage: "Your family wish has been saved with this baby keepsake.",
      }),
      mediaModule(
        "Add the first photos everyone will want to see again: tiny hands, sleepy faces, homecoming, family cuddles, or one tiny outfit.",
        "Baby Gallery"
      ),
      growthTimelineModule("Growth Timeline", [
        {
          id: createId("growth"),
          label: "Newborn days",
          dateLabel: "",
          note: "Save the first days: tiny details, first home photos, and the earliest family memories.",
          iconType: "heart",
          linkedModuleId: "",
        },
        {
          id: createId("growth"),
          label: "One month",
          dateLabel: "",
          note: "Add a note about what changed, what they loved, and the little personality already appearing.",
          iconType: "photo",
          linkedModuleId: "",
        },
        {
          id: createId("growth"),
          label: "Next tiny chapter",
          dateLabel: "",
          note: "Keep adding growth moments so this NFC gift becomes a living baby album.",
          iconType: "clock",
          linkedModuleId: "",
        },
      ]),
      voiceModule("A Message for Later", "Record a welcome, blessing, or tiny memory the family can replay later."),
      finalKeepsakeModule(
        "Keep This Beginning",
        "From Family",
        "A first keepsake for a story just beginning.",
        "Come back when you want to see how loved this beginning was."
      ),
    ],
  },

  thank_you: {
    data: baseData("Thank You", "Thank You", "A little gratitude gift", "pearl_rose", "layer_by_layer"),
    modules: [
      textModule(
        "What This Meant",
        "This is a small way to say something clearly: what you did mattered. It changed the day, lightened the load, or made something possible, and I do not want that kindness to pass by unmarked."
      ),
      surpriseModule(
        "Open This First",
        "Your kindness made a real difference. This is a little gift back, because some thank-yous deserve more than a quick text.",
        "Open before the thank-you note"
      ),
      finalKeepsakeModule(
        "Keep This Thanks",
        "With Gratitude",
        "A small keepsake for a kindness that mattered.",
        "This is here whenever you need to know it was appreciated."
      ),
    ],
  },
};

export const MODULE_DISPLAY_NAMES = {
  primary_media: "Photo Story",
  text: "Letter",
  song_dedication: "Music Moment",
  love_coupons: "Coupons",
  voice_postcard: "Voice + Photo Postcard",
  instant_camera: "Instant Camera",
  memory_mosaic: "Memory Mosaic",
  geometric_decrypt: "Rub to Reveal",
  time_together: "Time Together",
  love_promise: "Promise",
  affection_tapper: "Tap to Unlock",
  wisdom: "Wisdom",
  guestbook: "Blessing Wall",
  timeline: "Timeline",
  memory_map: "Memory Map",
  then_now: "Then & Now",
  frame_gallery: "Frame Gallery",
  surprise_box: "Open When",
  flip_card: "Tiny Museum",
  tiny_museum: "Tiny Museum",
  reaction: "Polaroid Booth",
  polaroid_booth: "Polaroid Booth",
  keepsake_poster: "Final Keepsake Card",
};
